import { QueueObject } from 'async';
import { CancelTokenSource } from 'axios';
import { injectable, interfaces } from 'inversify';
import { IObservableArray, makeObservable, observable } from 'mobx';
import { Plan } from '../user/user.store';

export interface IAttachmentUploadData {
  id: string;
  workspaceId: string;
  parentId: string | undefined;
  file: File;
}

export interface IAttachmentUploadProgressData {
  progress: number;
  id: string;
  name: string;
  isSuccess: boolean;
}

export enum UploadStatus {
  Valid = 'valid',
  InvalidSize = 'invalid_size',
  DuplicatedName = 'duplicated_file_name',
  DuplicatedExtension = 'duplicated_extension',
}

export interface IPreparedFile {
  file: File;
  status: UploadStatus;
  workspaceId: string;
  parentId: string | undefined;
}

export interface IWillAttachmentUploadExceedsLimitDto {
  willExceedsAvailableSpace: boolean;
  ownerPlan: Plan;
  areAllSuccessful: boolean;
}

interface ICurrentUpload {
  id: string;
  cancelToken: CancelTokenSource;
}

export interface ICurrentDownloadAttachment {
  id: string;
  isBatch: boolean;
}

export interface IAttachmentUploadStore {
  progressData: IObservableArray<IAttachmentUploadProgressData>;
  preparedFiles: IObservableArray<IPreparedFile>;
  willExceedsLimit: IWillAttachmentUploadExceedsLimitDto;
  currentUploads: IObservableArray<ICurrentUpload>;
  uploadQueue: QueueObject<IAttachmentUploadData>;
  currentDownloadAttachment: IObservableArray<ICurrentDownloadAttachment>;
}

export namespace IAttachmentUploadStore {
  export const $: interfaces.ServiceIdentifier<IAttachmentUploadStore> = Symbol('ATTACHMENT_UPLOAD_STORE');
}

@injectable()
export class AttachmentUploadStore implements IAttachmentUploadStore {
  progressData: IObservableArray<IAttachmentUploadProgressData> = observable.array([]);

  preparedFiles: IObservableArray<IPreparedFile> = observable.array([]);

  willExceedsLimit: IWillAttachmentUploadExceedsLimitDto = {
    areAllSuccessful: true,
    ownerPlan: Plan.Free,
    willExceedsAvailableSpace: false,
  };

  currentDownloadAttachment: IObservableArray<ICurrentDownloadAttachment> = observable.array([]);

  currentUploads: IObservableArray<ICurrentUpload> = observable.array([]);

  uploadQueue!: QueueObject<IAttachmentUploadData>;

  constructor() {
    makeObservable(this, {
      progressData: observable,
      preparedFiles: observable,
      willExceedsLimit: observable,
      currentDownloadAttachment: observable,
      currentUploads: observable,
    });
  }
}
